/**
 * Every review this account has open.
 *
 * The landing page, so it carries the empty state too — and the empty state has
 * to explain the one thing that is not obvious about this product: reviews do
 * not start here. They start in Cloud, because that is where the course file
 * lives and where the only browser that can decrypt it is sitting.
 */

import { useEffect, useState } from 'react';
import { api, ApiError, type ProjectWire } from '../api';
import { ds } from '../ds';
import { Link } from '../router';
import { useApp } from '../state';
import { Wordmark } from '../components/Wordmark';
import { display, formatWhen, LAYOUT, mono, monoPlain, RADIUS } from '../theme';

export function ProjectList() {
  const { me } = useApp();
  const [projects, setProjects] = useState<ProjectWire[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.projects
      .list()
      .then((r) => live && setProjects(r.projects))
      .catch(
        (e) => live && setError(e instanceof ApiError ? e.message : 'Your reviews would not load.')
      );
    return () => {
      live = false;
    };
  }, []);

  const cap = me
    ? me.projectsAllowed === -1
      ? `${me.projectsOpen} open · no limit`
      : `${me.projectsOpen} of ${me.projectsAllowed} open`
    : '';

  const G = LAYOUT.gutter;

  return (
    // The 2026-09-09 app chrome: a violet ground, the bar on it with no divider,
    // and the list itself a neutral panel inset by one gutter.
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        background: 'var(--app-ground)',
        display: 'flex',
        flexDirection: 'column',
        gap: G,
        padding: G
      }}
    >
      <header
        style={{
          height: LAYOUT.topbar,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 8px'
        }}
      >
        <Wordmark reversed size={15} />
        <span style={{ flex: 1 }} />
        <span style={mono}>{cap}</span>
      </header>

      <div
        style={{
          flex: 1,
          background: 'var(--app-panel)',
          borderRadius: RADIUS.appPanel
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 32px 60px' }}>
          {error && <p style={{ color: 'var(--danger-500)' }}>{error}</p>}

          {projects && projects.length === 0 && <Empty />}

          {projects?.map((p) => (
            <Link key={p.id} to={`/p/${p.id}`}>
              {/* A card, and the click target — so this is the one place in the
                  app that takes the readme's hover-lift. */}
              <div
                className="mv-lift"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '16px 18px',
                  marginBottom: 10,
                  borderRadius: RADIUS.card,
                  background: 'var(--surface-card)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text-strong)', fontSize: 15, fontWeight: 500 }}>
                    {p.title}
                  </div>
                  <div style={{ ...monoPlain, marginTop: 4 }}>
                    {p.lessonCount} lesson{p.lessonCount === 1 ? '' : 's'} · {p.blockCount} block
                    {p.blockCount === 1 ? '' : 's'} · sent {formatWhen(p.createdAt)}
                  </div>
                </div>
                {p.state === 'importing' && <span style={mono}>uploading</span>}
                {p.missingMedia.length > 0 && (
                  <span style={{ ...mono, color: 'var(--danger-500)' }}>media missing</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function Empty() {
  const { Icon } = ds();
  return (
    <div style={{ textAlign: 'center', padding: '70px 20px' }}>
      <Icon name="message-square" size={24} />
      <h1 style={{ ...display(28), marginTop: 14 }}>Nothing here yet.</h1>
      <p style={{ color: 'var(--text-body)', maxWidth: 460, margin: '12px auto 0', lineHeight: 1.6 }}>
        Reviews start in Mauvely Cloud — find a <code>.course</code> file, and send it for
        review from the row it sits on. Reviewers open a link, with no account and no password.
      </p>
      <p style={{ marginTop: 16 }}>
        <a href="https://cloud.mauvely.com" target="_blank" rel="noopener">
          Open Mauvely Cloud
        </a>
      </p>
    </div>
  );
}
